/**
 * The room behind the chimes, on its own WebGL context at reduced resolution: deep ink, the
 * red/black diagonal wandering so slowly it is felt rather than seen, three lancet light
 * falls breathing and brightening with the wind, dust turning in them, a soft vignette.
 * Falls back to a static 2D gradient if WebGL is unavailable. Far below the logo's
 * contrast by design: it is a room, not a picture.
 */
import type { Bus } from '../bus';
import type { Quality } from '../types';
import { damp } from '../util/math';

export interface AtmosOptions {
  canvas: HTMLCanvasElement;
  quality: Quality;
  bus: Bus;
}

export interface AtmosHandle {
  /** draw for time `now` (seconds). gust 0..1 is the current wind strength. */
  render(now: number, dt: number, gust: number): void;
  resize(): void;
  /** gentle parallax from the primary pointer, both in -1..1 */
  setParallax(nx: number, ny: number): void;
  dispose(): void;
}

export const ATMOS = {
  /** render scale relative to the DPR-capped viewport */
  scale: { full: 0.5, lite: 0.25 } as Record<Quality['tier'], number>,
  /** e-fold seconds of the chord bloom (visible for roughly 3x this) */
  chordDecay: 1.35,
  chordRise: 10,
  gustRate: 1.6,
  parallaxRate: 3,
  /** motion multiplier under prefers-reduced-motion */
  reducedMotion: 0.35,
} as const;

const VERT = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform vec2 uRes;
uniform float uTime;
uniform float uGust;
uniform vec2 uPar;
uniform float uChord;
uniform float uSeed;
uniform float uMotion;

float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}
float vnoise(vec2 p) {
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash12(i), b = hash12(i + vec2(1.0, 0.0)), c = hash12(i + vec2(0.0, 1.0)), d = hash12(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float s = 0.0, a = 0.5;
  for (int i = 0; i < 3; i++) { s += a * vnoise(p); p = p * 2.03 + 17.1; a *= 0.5; }
  return s;
}

// a lancet-shaped fall of light: gaussian across, tapering to a point at the top
float lancet(vec2 q, vec2 base, float w, float h, float taper) {
  float y = (q.y - base.y) / h;
  if (y < -0.4 || y > 1.15) return 0.0;
  float ww = w * mix(1.0, 0.16, smoothstep(taper, 1.0, y));
  float x = (q.x - base.x) / ww;
  float fx = exp(-x * x * 1.7);
  float fy = smoothstep(-0.4, 0.1, y) * (1.0 - smoothstep(0.86, 1.1, y));
  return fx * fy;
}

void main() {
  vec2 uv = gl_FragCoord.xy / uRes;
  float aspect = uRes.x / uRes.y;
  vec2 q = vec2((uv.x - 0.5) * aspect, uv.y - 0.5);
  float t = uTime * uMotion;

  vec3 ink = vec3(0.043, 0.039, 0.047);
  vec3 red = vec3(0.090, 0.051, 0.067);

  // the diagonal: red lower-right, black upper-left, drifting
  float ang = radians(45.0) + 0.09 * (fbm(vec2(t * 0.013, uSeed)) - 0.5) * 2.0;
  vec2 nrm = vec2(sin(ang), -cos(ang));
  float shift = 0.07 * (fbm(vec2(uSeed + 3.0, t * 0.011)) - 0.5) * 2.0;
  float dd = dot(q, nrm) + shift + 0.05 * (fbm(q * 1.4 + t * 0.017) - 0.5);
  float side = smoothstep(-0.24, 0.24, dd);
  vec3 col = mix(ink, red, side);
  col += vec3(0.028, 0.009, 0.007) * exp(-dd * dd / (2.0 * 0.075 * 0.075));

  // three lancet light falls
  vec2 par = uPar * vec2(0.035 * aspect, 0.02);
  float b0 = 0.8 + 0.2 * sin(t * 0.11);
  float b1 = 0.8 + 0.2 * sin(t * 0.087 + 2.1);
  float b2 = 0.8 + 0.2 * sin(t * 0.097 + 4.0);
  float gk = 1.0 + 0.6 * uGust;
  vec3 warm = vec3(0.34, 0.23, 0.14);
  vec3 cool = vec3(0.14, 0.18, 0.28);
  vec3 warm2 = vec3(0.30, 0.19, 0.14);
  float wbase = 0.03 + 0.05 * aspect;
  float I0 = lancet(q, vec2(-0.31 * aspect, -0.56) + par * 1.3, wbase, 1.0, 0.5);
  float I1 = lancet(q, vec2(0.02 * aspect, -0.62) + par * 1.0, wbase * 0.85, 1.06, 0.55);
  float I2 = lancet(q, vec2(0.33 * aspect, -0.58) + par * 0.75, wbase * 1.1, 0.96, 0.5);
  col += warm * I0 * (0.058 * b0 * gk + 0.075 * uChord);
  col += cool * I1 * (0.05 * b1 * gk + 0.03 * uChord);
  col += warm2 * I2 * (0.052 * b2 * gk + 0.07 * uChord);
  float light = I0 + I1 + I2;
  // a chord warms the whole room for a few seconds
  col += vec3(0.05, 0.027, 0.015) * uChord * (0.45 + 0.55 * light);

  // dust: two layers of motes, one per grid cell, each turning about its own centre;
  // gust widens and speeds the turning
  float dust = 0.0;
  for (int layer = 0; layer < 2; layer++) {
    float fl = float(layer);
    float cs = 0.07 + 0.05 * fl;
    vec2 flow = vec2(t * (0.005 + 0.004 * fl), t * 0.0025 * (1.0 + fl));
    vec2 p = (q + flow) / cs;
    vec2 ci = floor(p);
    for (int j = -1; j <= 1; j++) {
      for (int i = -1; i <= 1; i++) {
        vec2 cell = ci + vec2(float(i), float(j));
        float h1 = hash12(cell + 7.0 * fl), h2 = hash12(cell + 13.0 + fl);
        float ph = t * (0.12 + 0.3 * h2) * (1.0 + 2.5 * uGust) + h1 * 6.283;
        vec2 orbit = vec2(cos(ph), sin(ph * 0.9 + h2)) * (0.1 + 0.28 * uGust);
        vec2 mp = cell + vec2(h1, h2) * 0.8 + 0.1 + orbit;
        float dist = length(p - mp) * cs * uRes.y;
        float r = 0.8 + 0.7 * hash12(cell + 3.1) + 0.5 * fl;
        dust += exp(-dist * dist / (r * r)) * (0.3 + 0.7 * hash12(cell + 5.3));
      }
    }
  }
  col += vec3(0.9, 0.82, 0.7) * dust * (0.03 + 0.16 * light) * (1.0 + 0.5 * uChord);

  // vignette
  float v = length(vec2(q.x * 0.72, q.y * 1.15));
  col *= 1.0 - 0.42 * smoothstep(0.3, 0.95, v);

  // dither so the dark gradients do not band
  col += (hash12(gl_FragCoord.xy + fract(uTime) * 61.0) - 0.5) * (1.5 / 255.0);
  gl_FragColor = vec4(col, 1.0);
}
`;

export function createAtmos(opts: AtmosOptions): AtmosHandle {
  const { canvas, quality, bus } = opts;
  const attrs: WebGLContextAttributes = { alpha: false, antialias: false, depth: false, stencil: false, premultipliedAlpha: false, powerPreference: 'low-power', preserveDrawingBuffer: false };
  const gl = (canvas.getContext('webgl2', attrs) ?? canvas.getContext('webgl', attrs)) as WebGLRenderingContext | WebGL2RenderingContext | null;
  if (!gl) return createFallback(opts);

  const program = buildProgram(gl, VERT, FRAG);
  if (!program) return createFallback(opts);
  gl.useProgram(program);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(program, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
  const U = (name: string) => gl.getUniformLocation(program, name);
  const uRes = U('uRes'), uTime = U('uTime'), uGust = U('uGust'), uPar = U('uPar'), uChord = U('uChord'), uSeed = U('uSeed'), uMotion = U('uMotion');
  gl.uniform1f(uSeed, 4.7);
  gl.uniform1f(uMotion, quality.reducedMotion ? ATMOS.reducedMotion : 1);
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.BLEND);

  let wantX = 0, wantY = 0, px = 0, py = 0;
  let gustEased = 0;
  let chord = 0, chordWant = 0;
  const offChord = bus.on('chord', ({ strength }) => { chordWant = Math.max(chordWant, 0.6 + 0.4 * strength); });

  const resize = () => {
    const dpr = Math.min(devicePixelRatio || 1, quality.dprCap);
    const s = ATMOS.scale[quality.tier];
    canvas.width = Math.max(1, Math.round(innerWidth * dpr * s));
    canvas.height = Math.max(1, Math.round(innerHeight * dpr * s));
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.uniform2f(uRes, canvas.width, canvas.height);
  };
  resize();

  return {
    render(now, dt, gust) {
      px = damp(px, wantX, ATMOS.parallaxRate, dt);
      py = damp(py, wantY, ATMOS.parallaxRate, dt);
      gustEased = damp(gustEased, gust, ATMOS.gustRate, dt);
      chord = damp(chord, chordWant, ATMOS.chordRise, dt);
      chordWant *= Math.exp(-dt / ATMOS.chordDecay);
      gl.uniform1f(uTime, now);
      gl.uniform1f(uGust, gustEased);
      gl.uniform2f(uPar, px, -py);
      gl.uniform1f(uChord, chord);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    },
    resize,
    setParallax(nx, ny) { wantX = Math.max(-1, Math.min(1, nx)); wantY = Math.max(-1, Math.min(1, ny)); },
    dispose() {
      offChord();
      gl.deleteBuffer(buf);
      gl.deleteProgram(program);
      const lose = gl.getExtension('WEBGL_lose_context');
      lose?.loseContext();
    },
  };
}

function buildProgram(gl: WebGLRenderingContext, vs: string, fs: string): WebGLProgram | null {
  const compile = (type: number, src: string) => {
    const sh = gl.createShader(type)!;
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
      console.warn('[atmos] shader failed', gl.getShaderInfoLog(sh));
      gl.deleteShader(sh);
      return null;
    }
    return sh;
  };
  const v = compile(gl.VERTEX_SHADER, vs);
  const f = compile(gl.FRAGMENT_SHADER, fs);
  if (!v || !f) return null;
  const p = gl.createProgram()!;
  gl.attachShader(p, v); gl.attachShader(p, f);
  gl.linkProgram(p);
  gl.deleteShader(v); gl.deleteShader(f);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.warn('[atmos] link failed', gl.getProgramInfoLog(p));
    gl.deleteProgram(p);
    return null;
  }
  return p;
}

/** no WebGL at all: the diagonal as a still gradient */
function createFallback(opts: AtmosOptions): AtmosHandle {
  const { canvas } = opts;
  const ctx = canvas.getContext('2d');
  function paint() {
    const w = innerWidth, h = innerHeight;
    canvas.width = w; canvas.height = h;
    if (!ctx) return;
    const g = ctx.createLinearGradient(0, h, w, 0);
    g.addColorStop(0, '#0b0a0c');
    g.addColorStop(0.5, '#0e0b0e');
    g.addColorStop(1, '#170d11');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  }
  paint();
  return { render() {}, resize: paint, setParallax() {}, dispose() {} };
}
